import i18n from "src/i18n/config";
import { z } from "zod";

import { CompositePolicyRule } from "../types";
import { invalidPackage, packageName, toBoolean, toList } from "../utils";

const autostartPreferences = [
    {
        command: "changePreference",
        package: "com.emteria.settings",
        category: "com.emteria.settings_preferences",
        key: "autostart_enabled",
        type: "bool",
    },
    {
        command: "changePreference",
        package: "com.emteria.kiosk",
        category: "com.emteria.kiosk_preferences",
        key: "autostart_enabled",
        type: "bool",
    },
    {
        command: "changePreference",
        package: "com.emteria.settings",
        category: "com.emteria.settings_preferences",
        key: "autostart_apps",
        type: "set",
    },
    {
        command: "changePreference",
        package: "com.emteria.kiosk",
        category: "com.emteria.kiosk_preferences",
        key: "autostart_apps",
        type: "set",
    },
];

const positionPrefix = /^\d+;/;

function fromList(value: unknown) {
    return toList(value).map((entry) => entry.replace(positionPrefix, ""));
}

const autostartSchema = z
    .object({ enabled: z.boolean(), apps: z.array(z.string()) })
    .superRefine(({ enabled, apps }, context) => {
        if (enabled && apps.some((name) => !packageName.test(name))) {
            context.addIssue({ code: "custom", path: ["apps"], message: invalidPackage() });
        }
    });

export const autostart: CompositePolicyRule<z.infer<typeof autostartSchema>> = {
    id: "autostart",
    title: () => i18n.t("modals:addRule.rules.autostart.title"),
    description: () => i18n.t("modals:addRule.rules.autostart.description"),
    details: () => i18n.t("modals:addRule.rules.autostart.details"),
    summary: (_, { enabled, apps }) => {
        if (!enabled) {
            return i18n.t("modals:addRule.rules.autostart.summaryDisabled");
        }
        return apps.length
            ? i18n.t("modals:addRule.rules.autostart.summaryApps", { count: apps.length })
            : i18n.t("modals:addRule.rules.autostart.summaryEnabled");
    },
    categories: ["kiosk"],
    flags: [],
    fields: [
        { type: "boolean", name: "enabled", label: () => i18n.t("common:label.enabled") },
        {
            type: "list",
            name: "apps",
            label: () => i18n.t("modals:addRule.rules.autostart.apps"),
            itemLabel: () => i18n.t("common:label.packageName"),
            itemPattern: packageName,
            itemError: invalidPackage,
            dependsOn: { name: "enabled", value: true },
            itemSource: "uploadedApps",
        },
    ],
    defaultValues: { enabled: true, apps: [] },
    schema: autostartSchema,
    command: {
        command: "processSubcommands",
        success: "any",
        subcommands: autostartPreferences,
    },
    ignoredKeys: ["enabled", "list"],
    toValues: ({ enabled, apps }) => {
        const mode = String(enabled);
        const packages = enabled ? apps : [];
        const list = packages.length ? packages.map((name, position) => `${position};${name}`).join(",") : null;
        return [mode, mode, list, list];
    },
    fromValues: ([settingsMode, kioskMode, settingsList, kioskList]) => {
        const enabled = settingsMode === kioskMode ? toBoolean(settingsMode) : undefined;
        if (enabled === undefined || settingsList !== kioskList) {
            return null;
        }
        return { enabled, apps: fromList(settingsList) };
    },
};
